import styled from 'styled-components'

import theme from '@/src/config/theme'
import {Image} from '@/src/shared/ui/image'
import {Link} from '@/src/shared/ui/link'

import {Banner, BannerPosition} from './types'
import {sanitizeHtml} from '@/src/shared/utils/functions'

interface IProps {
  banner: Banner
}

const Wrapper = styled.div`
  align-self: baseline;
  display: flex;
  justify-content: center;
  width: 100%;
  background: ${theme.colors.bgDark};
`

const LinkContainer = styled(Link)`
  position: relative;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  img {
    width: 100%;
    height: auto;
    object-fit: unset;
  }
`

const CaptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 15px 0;

  @media (min-width: 600px) {
    padding: unset;
  }
`

const Title = styled.div<{$position?: BannerPosition}>`
  font-size: 1.5rem;
  font-weight: 450;
  text-align: center;

  left: 0;
  top: 0;
  right: 0;
  bottom: 0;

  @media (min-width: 600px) {
    font-size: 3rem;
    text-align: unset;
  }
`

const Description = styled.div`
  font-weight: 400;
  font-size: 1.2rem;

  text-align: center;

  @media (min-width: 600px) {
    text-align: unset;
  }
`

const HeroContainer = styled.div`
  display: flex;

  width: 100%;
  max-width: ${theme.sizes.container}px;

  padding: ${theme.token.layout.pagePadding}px;
  color: ${theme.colors.white};

  :first-child:hover {
    color: ${theme.colors.primary};
  }

  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);

  @media (min-width: 600px) {
    position: absolute;

    height: 100%;

    &[data-position='top_center'] ${CaptionContainer} {
      align-items: center;
    }

    &[data-position='top_right'] ${CaptionContainer} {
      align-items: flex-end;
    }

    &[data-position='center_left'] ${CaptionContainer} {
      justify-content: center;
    }

    &[data-position='center'] ${CaptionContainer} {
      justify-content: center;
      align-items: center;
    }

    &[data-position='center_right'] ${CaptionContainer} {
      justify-content: center;
      align-items: flex-end;
    }

    &[data-position='bottom_left'] ${CaptionContainer} {
      justify-content: flex-end;
      align-items: flex-start;
    }

    &[data-position='bottom_center'] ${CaptionContainer} {
      justify-content: flex-end;
      align-items: center;
    }

    &[data-position='bottom_right'] ${CaptionContainer} {
      justify-content: flex-end;
      align-items: flex-end;
    }
  }

  @media (min-width: 1465px) {
    padding: 20px 0;
  }
`

const BannerComponent = ({banner}: IProps) => {
  return (
    <Wrapper>
      <LinkContainer href={banner.link} asChild>
        <Image
          preload
          src={`${banner.media.src}`}
          fetchPriority="high"
          loading="eager"
          alt=""
          width={theme.sizes.container}
          height={384}
          quality={100}
        />
        {banner.caption && (
          <HeroContainer data-position={banner.caption?.position ?? 'top_left'}>
            <CaptionContainer>
              {banner.caption?.title && (
                <Title $position={banner.caption.position}>{banner.caption?.title}</Title>
              )}
              {banner.caption?.description && (
                <Description
                  dangerouslySetInnerHTML={{__html: sanitizeHtml(banner.caption?.description)}}
                />
              )}
            </CaptionContainer>
          </HeroContainer>
        )}
      </LinkContainer>
    </Wrapper>
  )
}

export default BannerComponent
